import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatSortModule, Sort } from '@angular/material/sort';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, transferArrayItem } from '@angular/cdk/drag-drop';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import {
  DUE_FILTER_OPTIONS,
  initials,
  overdueDays,
  relatedTypeLabel,
  Task,
  TASK_BOARD_STATUSES,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  TASK_TYPE_OPTIONS,
  TaskLinkPrefill,
  taskPriorityClass,
  TaskStatus,
  taskStatusClass,
  TaskSummary,
} from '../task.model';
import { TaskFormComponent } from '../task-form/task-form.component';
import { LazyPicker, propertyPicker, staffPicker } from '../lazy-picker';

interface BoardLane {
  status: string;
  tasks: Task[];
}

@Component({
  selector: 'app-tasks-list',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    BreadcrumbComponent,
    PaginationComponent,
    MatSortModule,
    NgSelectModule,
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    TaskFormComponent,
  ],
  templateUrl: './tasks-list.component.html',
  styleUrls: ['./tasks-list.component.css'],
})
export class TasksListComponent implements OnInit, OnChanges {
  @ViewChild('taskForm') taskForm!: TaskFormComponent;
  @ViewChild('holdReasonModal') holdReasonModal!: TemplateRef<any>;

  /** Embedded mode (property / lead page): hides KPIs, board and most filters. */
  @Input() embedded = false;
  @Input() propertyId: string | null = null;
  @Input() relatedType: string | null = null;
  @Input() relatedId: string | null = null;
  /** Display name of the linked record, used to pre-fill the create form. */
  @Input() linkName: string | null = null;

  breadcrumbList = {
    breadcrumb_path: 'Dashboard',
    currentURL: 'Tasks',
    breadcrumb_url: '/admin',
  };

  view: 'list' | 'board' = 'list';
  tasks: Task[] = [];
  totalTasks = 0;
  page = 1;
  limit = 50;
  totalPage = 1;
  totalRows = 50;
  sortBy = 'created_at';
  orderBy = 'DESC';

  summary: TaskSummary | null = null;
  lanes: BoardLane[] = TASK_BOARD_STATUSES.map((status) => ({ status, tasks: [] }));
  private readonly boardLimit = 200;

  searchForm!: FormGroup;
  disableClear = false;
  typeOptions = TASK_TYPE_OPTIONS;
  statusOptions = TASK_STATUS_OPTIONS;
  priorityOptions = TASK_PRIORITY_OPTIONS;
  dueOptions = DUE_FILTER_OPTIONS;
  assignees: LazyPicker;
  properties: LazyPicker;

  // Hold-reason prompt state
  holdReason = '';

  // Permissions
  canAdd = false;
  canUpdate = false;
  canDelete = false;
  canViewAll = false;

  // template helpers
  statusClass = taskStatusClass;
  priorityClass = taskPriorityClass;
  initials = initials;
  overdueDays = overdueDays;
  relatedTypeLabel = relatedTypeLabel;

  constructor(
    private httpService: HttpServiceService,
    public loadingService: LoadingService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private fb: FormBuilder,
    private alertService: NgxToastrService,
    private modalService: NgbModal,
    private destroyRef: DestroyRef,
  ) {
    this.assignees = staffPicker(httpService, destroyRef, [{ value: 'me', label: 'Me' }]);
    this.properties = propertyPicker(httpService, destroyRef);
    this.initForms();
  }

  ngOnInit(): void {
    this.checkPermissions();
    if (this.embedded) this.limit = 10;
    this.searchForm.patchValue({ assignee_id: this.canViewAll ? null : 'me' }, { emitEvent: false });
    this.setupSearchListener();
    if (!this.embedded) {
      this.assignees.init();
      this.properties.init();
      this.getSummary();
    }
    this.handleSearch('');
  }

  ngOnChanges(changes: SimpleChanges): void {
    const linkChanged = changes['propertyId'] || changes['relatedId'] || changes['relatedType'];
    if (linkChanged && !linkChanged.firstChange) this.handleSearch('');
  }

  get prefill(): TaskLinkPrefill | null {
    if (this.propertyId) return { property_id: this.propertyId, property_name: this.linkName ?? undefined };
    if (this.relatedType && this.relatedId) {
      return { related_type: this.relatedType, related_id: this.relatedId, related_name: this.linkName ?? undefined };
    }
    return null;
  }

  initForms(): void {
    this.searchForm = this.fb.group({
      search: [''],
      assignee_id: [null],
      property_id: [null],
      task_type: [null],
      status: ['open'],
      priority: [''],
      due: [''],
      due_from: [''],
      due_to: [''],
    });
  }

  setupSearchListener(): void {
    this.searchForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleSearch(''));
  }

  checkPermissions(): void {
    const p = USER_PERMISSIONS.TASKS;
    this.canAdd = this.localStorageService.checkPermission(p.TASKS_ADD);
    this.canUpdate = this.localStorageService.checkPermission(p.TASKS_UPDATE);
    this.canDelete = this.localStorageService.checkPermission(p.TASKS_DELETE);
    this.canViewAll = this.localStorageService.checkPermission(p.TASKS_VIEW_ALL);
  }

  // ── Query building ─────────────────────────────────────────────────────
  private linkQuery(): string {
    if (this.propertyId) return `&property_id=${this.propertyId}`;
    if (this.relatedType && this.relatedId) return `&related_type=${this.relatedType}&related_id=${this.relatedId}`;
    return '';
  }

  private buildQuery(includeStatus = true): string {
    const f = this.searchForm.value;
    let q = this.linkQuery();
    this.disableClear = false;
    if (f.search) {
      q += `&search=${encodeURIComponent(f.search)}`;
      this.disableClear = true;
    }
    if (f.assignee_id) {
      q += `&assignee_id=${f.assignee_id}`;
      if (f.assignee_id !== 'me' || this.canViewAll) this.disableClear = true;
    }
    if (f.property_id && !this.propertyId) {
      q += `&property_id=${f.property_id}`;
      this.disableClear = true;
    }
    if (f.task_type) {
      q += `&task_type=${encodeURIComponent(f.task_type)}`;
      this.disableClear = true;
    }
    if (f.priority) {
      q += `&priority=${encodeURIComponent(f.priority)}`;
      this.disableClear = true;
    }
    if (includeStatus && f.status) {
      q += `&status=${encodeURIComponent(f.status)}`;
      if (f.status !== 'open') this.disableClear = true;
    }
    if (f.due) {
      if (f.due === 'range') {
        if (f.due_from || f.due_to) {
          q += `&due=range`;
          if (f.due_from) q += `&due_from=${f.due_from}`;
          if (f.due_to) q += `&due_to=${f.due_to}`;
        }
      } else {
        q += `&due=${f.due}`;
      }
      this.disableClear = true;
    }
    return q;
  }

  handleSearch(keepPage: string = ''): void {
    if (keepPage !== 'keepPage') this.page = 1;
    if (this.view === 'board') this.getBoard();
    else this.getAllTasks(this.buildQuery());
  }

  onDueChange(): void {
    const due = this.searchForm.value.due;
    if (due !== 'range') {
      this.searchForm.patchValue({ due_from: '', due_to: '' }, { emitEvent: false });
      this.handleSearch('');
    }
  }

  resetSearchForm(): void {
    this.searchForm.reset({
      search: '',
      assignee_id: this.canViewAll ? null : 'me',
      property_id: null,
      task_type: null,
      status: 'open',
      priority: '',
      due: '',
      due_from: '',
      due_to: '',
    });
    this.page = 1;
    this.disableClear = false;
    this.handleSearch('');
  }

  // ── Data ───────────────────────────────────────────────────────────────
  getSummary(): void {
    const a = this.searchForm.value.assignee_id;
    this.httpService.getTasksSummary(a ? `assignee_id=${a}` : '').subscribe({
      next: (response: any) => {
        if (response.success) this.summary = response.data;
      },
      error: () => {},
    });
  }

  getAllTasks(queryParams: string = ''): void {
    this.loadingService.show();
    this.httpService.getAllTasks(this.page, this.limit, this.sortBy, this.orderBy, queryParams).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.tasks = response.data.tasks;
          this.totalTasks = response.data.total;
          this.totalPage = response.data.pageCount;
          this.totalRows = this.tasks.length > this.limit ? this.limit : this.tasks.length;
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  getBoard(): void {
    // Board shows every lane regardless of the status filter.
    this.loadingService.show();
    this.httpService.getAllTasks(1, this.boardLimit, 'due_date', 'ASC', this.buildQuery(false)).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          const all: Task[] = response.data.tasks ?? [];
          this.lanes = TASK_BOARD_STATUSES.map((status) => ({
            status,
            tasks: all.filter((t) => t.status === status),
          }));
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  setView(view: 'list' | 'board'): void {
    if (this.view === view) return;
    this.view = view;
    this.handleSearch('');
  }

  sortData(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.orderBy = sort.direction.toUpperCase();
    } else {
      this.sortBy = 'created_at';
      this.orderBy = 'DESC';
    }
    this.page = 1;
    this.handleSearch('');
  }

  pageChange(newPage: number): void {
    if (this.page == newPage) return;
    this.page = newPage;
    this.handleSearch('keepPage');
  }

  reload(): void {
    this.handleSearch('keepPage');
    if (!this.embedded) this.getSummary();
  }

  // ── Row helpers ────────────────────────────────────────────────────────
  linkedName(task: Task): string {
    return task.property?.name || task.related?.name || '';
  }

  linkedSubline(task: Task): string {
    const parts: string[] = [];
    if (task.property) parts.push('Property');
    else if (task.related) parts.push(relatedTypeLabel(task.related.type));
    if (task.client_type) parts.push(task.client_type);
    return parts.join(' · ');
  }

  plannedWindow(task: Task): string {
    if (task.planned_start && task.planned_end) return `${task.planned_start}–${task.planned_end}`;
    return task.planned_start || task.planned_end || '';
  }

  attachmentCount(task: Task): number {
    return task.uploads?.length ?? 0;
  }

  // ── Navigation / CRUD ──────────────────────────────────────────────────
  viewTask(id: string): void {
    this.router.navigate(['/admin/tasks/view', id]);
  }

  openTaskModal(taskId?: string): void {
    this.taskForm.open(taskId);
  }

  deleteTask(id: string): void {
    if (!confirm('Are you sure you want to delete this task?')) return;
    this.loadingService.show();
    this.httpService.deleteTask(id).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message, 'toast-top-right');
          this.reload();
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  // ── Board drag & drop ──────────────────────────────────────────────────
  laneIds(): string[] {
    return this.lanes.map((l) => 'lane-' + l.status.replace(/\s+/g, '-'));
  }

  laneId(lane: BoardLane): string {
    return 'lane-' + lane.status.replace(/\s+/g, '-');
  }

  onDrop(event: CdkDragDrop<Task[]>, lane: BoardLane): void {
    if (event.previousContainer === event.container) return;
    const task: Task = event.previousContainer.data[event.previousIndex];
    const fromLane = this.lanes.find((l) => l.tasks === event.previousContainer.data);
    const revert = () => {
      if (!fromLane) return;
      const idx = lane.tasks.indexOf(task);
      if (idx > -1) lane.tasks.splice(idx, 1);
      fromLane.tasks.splice(event.previousIndex, 0, task);
    };

    transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);

    if (lane.status === TaskStatus.ON_HOLD) {
      this.promptHoldReason().then(
        (reason) => this.changeStatus(task, lane.status, reason, revert),
        () => revert(),
      );
    } else {
      this.changeStatus(task, lane.status, undefined, revert);
    }
  }

  /** Opens the small hold-reason modal; resolves with the reason, rejects on cancel. */
  promptHoldReason(): Promise<string> {
    this.holdReason = '';
    return this.modalService
      .open(this.holdReasonModal, { centered: true, backdrop: 'static' })
      .result.then((reason: string) => {
        if (!reason || !reason.trim()) throw new Error('empty');
        return reason.trim();
      });
  }

  private changeStatus(task: Task, status: string, hold_reason?: string, onError?: () => void): void {
    const prev = task.status;
    task.status = status;
    if (hold_reason) task.hold_reason = hold_reason;
    this.httpService.updateTaskStatus(task.id, hold_reason ? { status, hold_reason } : { status }).subscribe({
      next: (response: any) => {
        if (response.success) {
          if (response.data?.id) Object.assign(task, response.data);
          this.alertService.success(response.message || 'Status updated', 'toast-top-right');
          if (!this.embedded) this.getSummary();
        } else {
          task.status = prev;
          onError?.();
        }
      },
      error: () => {
        task.status = prev;
        onError?.();
      },
    });
  }
}
